import React, { type ReactNode, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';
import { getJiraUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getJiraEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSummarizedAdfContent } from '../../../../../../common/utils/summarized-adf-content';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchJiraFilter_AtlassianSearchPage$key } from './__generated__/searchJiraFilter_AtlassianSearchPage.graphql';

export const SearchJiraFilter = ({
	node,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchJiraFilter_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchJiraFilter_AtlassianSearchPage on SearchResultJiraFilter {
				__typename
				id
				title
				url
				type
				description
				lastModifiedDate
				iconUrl
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);
	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const url = useMemo(() => {
		return getJiraUrlWithAnalyticsQueryParams({
			url: data.url,
			searchSessionId,
			resultId: data.id,
		});
	}, [data.url, data.id, searchSessionId]);

	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(data.title, searchQuery.split(' '));

		return !highlightedTitle ? data.title : enrichExcerpt(highlightedTitle);
	}, [data.title, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = getSummarizedAdfContent(data.description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [data.description, searchQuery]);

	const metadataParts: [string, ReactNode][] = useMemo(() => {
		const parts = [
			['type', <SearchResultType type={data.type as SearchClientResultType} />],
			[
				'lastModified',
				data.lastModifiedDate ? <LastModified lastModified={data.lastModifiedDate} /> : null,
			],
		];
		return parts.filter(Boolean) as [string, ReactNode][];
	}, [data.type, data.lastModifiedDate]);

	const icon = getJiraEntityIcon({
		iconUrl: data.iconUrl,
		type: data.type,
		isIssue: false,
		size: 'medium',
	});

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Jira}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			title={enrichedTitle}
			headerMetadataParts={metadataParts}
			description={<DescriptionBody description={enrichedDescription} />}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchJiraFilter = (
	node?: any,
): node is searchJiraFilter_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultJiraFilter';
};
